import { AsyncPipe, DatePipe, NgClass, TitleCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faArrowsRotate,
  faDownload,
  faFileContract,
  faPlus,
  faShare,
  faSpinner,
  faTimes,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColDef, GridApi, SelectionChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { saveAs } from 'file-saver';
import { QuillEditorComponent } from 'ngx-quill';
import { MessageService } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { Chip } from 'primeng/chip';
import { Dialog } from 'primeng/dialog';
import { Divider } from 'primeng/divider';
import { Drawer } from 'primeng/drawer';
import { FloatLabel } from 'primeng/floatlabel';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Select, SelectModule } from 'primeng/select';
import { Tag } from 'primeng/tag';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { AgButtonCellComponent } from 'src/app/components/agnostic-grid/components/ag-button-cell/ag-button-cell.component';
import { GridData } from 'src/app/components/agnostic-grid/models/grid-data.model';
import { DocumentCodeCategory } from 'src/app/models/labels/document-code-category.model';
import { DocumentCode } from 'src/app/models/labels/document-code.model';
import { DocumentCodeService } from 'src/app/services/labels/document-code.service';
import { AgTagComponent } from 'src/app/components/agnostic-grid/components/ag-tag/ag-tag.component';
import { RgsHttpErrorResponse } from 'src/app/shared/models/http-error.model';
import { FormUtils } from 'src/app/shared/utils/form.utils';
import { ToastUtils } from 'src/app/shared/utils/toast.utils';
import { minOneItem } from 'src/app/shared/validators/min-one-item.validator';
import { AgnosticGridsComponent } from '../../../../components/agnostic-grid/agnostic-grids/agnostic-grids.component';
import { RemoveReportTaskResponse } from './models/remove-report-task-response.model';
import { Task, TaskReportType, TaskStatus } from './models/task.model';
import { ShareOptions, TaskService } from './services/task.service';

@Component({
  selector: 'app-tasks-sidebar',
  templateUrl: './tasks-sidebar.component.html',
  styleUrls: ['./tasks-sidebar.component.css'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    Drawer,
    ButtonDirective,
    AgnosticGridsComponent,
    FaIconComponent,
    Dialog,
    Divider,
    Tag,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    FloatLabel,
    InputGroup,
    InputText,
    InputGroupAddon,
    Message,
    Chip,
    AsyncPipe,
    TitleCasePipe,
    TranslatePipe,
    SelectModule,
    QuillEditorComponent,
  ],
})
export class TasksSidebarComponent implements OnInit {
  private readonly translateSrv = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);
  private readonly documentCodeService = inject(DocumentCodeService);

  private readonly taskService = inject(TaskService);
  private readonly toastService = inject(MessageService);
  private readonly fb = inject(FormBuilder);
  private readonly ref = inject(ChangeDetectorRef);

  public readonly faTrash = faTrash;
  public readonly faShare = faShare;
  public readonly faDownload = faDownload;
  public readonly faPlus = faPlus;
  public readonly faTimes = faTimes;
  public readonly faSpinner = faSpinner;
  public readonly faArrowsRotate = faArrowsRotate;
  public readonly faFileContract = faFileContract;

  public readonly isInvalid = FormUtils.isInvalid;

  public selectedTasks: Task[] = [];
  public firstThreeSelectedTasks = signal<Task[]>([]);
  public readonly tasksSidebarVisible = signal(false);
  public readonly shareDialogVisible = signal(false);
  public emailFormControl = this.fb.control('', [Validators.email]);
  public shareEmailFormGroup = this.fb.nonNullable.group({
    ids: this.fb.nonNullable.control<Set<string>>(new Set<string>(), [Validators.required, minOneItem]),
    emails: this.fb.nonNullable.control<Set<string>>(new Set<string>(), [Validators.required, minOneItem]),
    description: this.fb.nonNullable.control<string>(''),
  });
  public loadingShareTasks = signal<boolean>(false);

  public tasks$ = this.taskService.items$.pipe(
    map((tasks) => {
      tasks.sort((a, b) => (this.getStatusIndex(a) > this.getStatusIndex(b) ? -1 : 1));
      return tasks;
    }),
  );
  public pendingTasks$ = this.tasks$.pipe(
    map((tasks) =>
      tasks.filter((task) => task.reportStatus === TaskStatus.RUNNING || task.reportStatus === TaskStatus.QUEUED),
    ),
  );
  public finishedTasks$ = this.tasks$.pipe(
    map((tasks) =>
      tasks.filter(
        (task) =>
          task.reportStatus === TaskStatus.COMPLETE ||
          task.reportStatus === TaskStatus.FAIL ||
          task.reportStatus === TaskStatus.DOWNLOADED,
      ),
    ),
  );
  public pendingTasksDependencies$: Observable<GridData<Task>> = this.pendingTasks$.pipe(
    map((tasks) => ({
      rawJsonData: tasks,
      structure: this.getColStructure('pending'),
      componentName: 'pendingTasks',
    })),
  );
  public loadingTasksIds$ = new BehaviorSubject<string[]>([]);
  public finishedTasksDependencies$: Observable<GridData<Task>> = combineLatest([
    this.finishedTasks$,
    this.loadingTasksIds$,
  ]).pipe(
    map(([tasks, loadingTasksIds]) => ({
      rawJsonData: tasks,
      structure: this.getColStructure('finished', loadingTasksIds),
      gridOptions: {
        rowSelection: {
          mode: 'multiRow',
          isRowSelectable: (rowNode) => !!rowNode.data && !loadingTasksIds?.includes(rowNode.data?.uuid),
          enableMultiSelectWithClick: true,
          checkboxes: true,
          headerCheckbox: true,
        },
      },
      componentName: 'finishedTasks',
    })),
  );

  public finishedTasksGridApi!: GridApi<Task>;

  public readonly documentCodesResource = rxResource({
    defaultValue: [],
    stream: () => this.documentCodeService.findAllByCategory(DocumentCodeCategory.REPORT_EMAIL),
  });

  public ngOnInit(): void {
    this.taskService.taskSidebarVisible$.subscribe((visible) => {
      this.tasksSidebarVisible.set(visible);
    });
  }

  public onSelectionChange(event: SelectionChangedEvent<Task>) {
    this.selectedTasks = event.api.getSelectedRows();
    this.firstThreeSelectedTasks.set(this.selectedTasks.slice(0, 3));
  }

  public onGridApiChange(gridApi: GridApi<Task>) {
    this.finishedTasksGridApi = gridApi;
  }

  public closeAllTaskSockets() {
    this.taskService.closeAllTaskSockets();
    this.taskService.createAllSockets();
  }

  public getColStructure(status: 'pending' | 'finished', loadingTasksIds?: string[]): ColDef<Task>[] {
    let cols: ColDef[] = [
      {
        field: 'name',
        headerName: this.translateSrv.instant('fields.name'),
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
        valueGetter: (params: ValueGetterParams<Task>) => params.data?.reportName,
      },
      {
        field: 'created',
        headerName: this.translateSrv.instant('fields.date'),
        sortable: true,
        flex: 1,
        resizable: true,
        maxWidth: 150,
        valueGetter: (params: ValueGetterParams<Task>) => {
          const createdDate = params?.data?.created;
          return createdDate ? new Date(createdDate) : null;
        },
        valueFormatter: (params) => this.datePipe.transform(params.value, 'dd-MM-yyyy HH:mm') || '',
      },
      {
        field: 'username',
        headerName: this.translateSrv.instant('fields.username'),
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
        maxWidth: 150,
      },
      {
        field: 'type',
        headerName: this.translateSrv.instant('fields.type'),
        sortable: true,
        filter: true,
        maxWidth: 110,
        cellRenderer: AgTagComponent<Task>,
        cellRendererParams: {
          label: (task: Task) => {
            switch (task.type) {
              case TaskReportType.INSPECTION:
                return this.translateSrv.instant('reportTasks.types.inspection');
              case TaskReportType.STRATEGY:
                return this.translateSrv.instant('reportTasks.types.strategy');
              case TaskReportType.SCENARIO:
                return this.translateSrv.instant('reportTasks.types.scenario');
              case TaskReportType.DATA_ANALYSIS:
                return this.translateSrv.instant('reportTasks.types.dataAnalysis');
            }
          },
          severity: (task: Task) =>
            'report-task-status report-task-status-' + task.type.toLowerCase().replace('_', '-'),
        },
      },
      {
        field: 'reportStatus',
        headerName: this.translateSrv.instant('fields.reportStatus'),
        sortable: true,
        filter: true,
        maxWidth: 110,
        cellRenderer: AgTagComponent<Task>,
        cellRendererParams: {
          label: (task: Task) => {
            let res = '';
            switch (task.reportStatus) {
              case TaskStatus.QUEUED:
                res = this.translateSrv.instant('reportTasks.status.queued');
                break;
              case TaskStatus.RUNNING:
                res = this.translateSrv.instant('reportTasks.status.running');
                break;
              case TaskStatus.COMPLETE:
                res = this.translateSrv.instant('reportTasks.status.complete');
                break;
              case TaskStatus.FAIL:
                res = this.translateSrv.instant('reportTasks.status.failed');
                break;
            }
            return res;
          },
          severity: (task: Task) => {
            let res = '';
            switch (task.reportStatus) {
              case TaskStatus.QUEUED:
                res = 'warn';
                break;
              case TaskStatus.RUNNING:
                res = 'primary';
                break;
              case TaskStatus.COMPLETE:
                res = 'success';
                break;
              case TaskStatus.FAIL:
                res = 'danger';
                break;
            }
            return res;
          },
        },
      },
    ];

    if (status === 'finished') {
      cols = [
        ...cols,
        {
          colId: 'download',
          sortable: false,
          filter: false,
          editable: false,
          maxWidth: 45,
          cellStyle: { display: 'flex', 'align-items': 'center' },
          cellRenderer: AgButtonCellComponent<Task>,
          cellRendererParams: {
            button: {
              icon: (task: Task) => (loadingTasksIds?.includes(task.uuid) ? faSpinner : faDownload),
              tooltip: this.translateSrv.instant('buttons.download', {
                name: this.translateSrv.instant('collections.tasks.singular'),
              }),
              onClick: this.onDownload.bind(this),
              disabled: (task: Task) =>
                (task.reportStatus !== TaskStatus.COMPLETE && task.reportStatus !== TaskStatus.DOWNLOADED) ||
                loadingTasksIds?.includes(task.uuid),
            },
          },
        },
      ] as ColDef<Task>[];
    }

    return cols;
  }

  public onShare(tasks: Task[]) {
    this.shareEmailFormGroup.controls.ids.setValue(new Set(tasks.map((task) => task.uuid)));
    this.shareDialogVisible.set(true);
  }

  public onHideShareDialog() {
    this.shareDialogVisible.set(false);
    this.shareEmailFormGroup.reset();
    this.emailFormControl.reset();
  }

  public onSubmitShareTasks() {
    if (!this.shareEmailFormGroup.valid) {
      FormUtils.markAllAsDirtyAndTouched(this.shareEmailFormGroup);
      return;
    }

    const shareOptions = this.shareEmailFormGroup.getRawValue() as ShareOptions;

    this.loadingShareTasks.set(true);
    this.taskService.shareTask(shareOptions).subscribe({
      next: () => {
        this.loadingShareTasks.set(false);
        this.toastService.add({
          severity: 'success',
          summary: this.translateSrv.instant('messages.shareTask.success.summary'),
          detail: this.translateSrv.instant('messages.shareTask.success.detail'),
        });
        this.onHideShareDialog();
      },
      error: (err: RgsHttpErrorResponse) => {
        this.loadingShareTasks.set(false);

        const summary = this.translateSrv.instant('messages.shareTask.error.summary');
        ToastUtils.showError(this.toastService, summary, err.error);
      },
    });
  }

  public onAddEmail() {
    if (!this.emailFormControl.valid) {
      FormUtils.markAllAsDirtyAndTouched(this.emailFormControl);
      return;
    }
    const email = this.emailFormControl.getRawValue() as string;

    if (!email) {
      return;
    }

    const emails = this.shareEmailFormGroup.controls.emails.getRawValue();
    this.shareEmailFormGroup.controls.emails.setValue(new Set([...emails, email]));
    this.emailFormControl.reset();
    this.ref.detectChanges();
  }

  public onRemoveEmail(email: string) {
    const emails = this.shareEmailFormGroup.controls.emails.getRawValue();
    emails.delete(email);
    this.shareEmailFormGroup.controls.emails.setValue(new Set(emails));
  }

  public onDownload(task: Task | Task[]) {
    if (Array.isArray(task)) {
      if (task.length === 1) {
        this.downloadTask(task[0]);
        return;
      }
      this.downloadTasks(
        task.filter((t) => t.reportStatus === TaskStatus.COMPLETE || t.reportStatus === TaskStatus.DOWNLOADED),
      );
    } else {
      this.downloadTask(task);
    }
  }

  public onDelete(tasks: Task[]) {
    tasks.forEach((task) => {
      this.taskService.deleteTask(task).then((response) => {
        if (response === RemoveReportTaskResponse.NOT_ALLOWED) {
          this.toastService.add({
            severity: 'warn',
            summary: this.translateSrv.instant('messages.deleteTask.notAllowed.summary'),
            detail: this.translateSrv.instant('messages.deleteTask.notAllowed.detail', {
              taskName: task.reportName,
            }),
          });
        }
      });
    });
  }

  public downloadTask(task: Task) {
    this.loadingTasksIds$.next([...this.loadingTasksIds$.value, task.uuid]);
    this.taskService.downloadResult(task).subscribe({
      next: (value) => {
        this.loadingTasksIds$.next(this.loadingTasksIds$.value.filter((uuid) => uuid !== task.uuid));
        const file = new File([value as BlobPart], `${task.reportName}.${Task.getMimeType(task)}`);
        saveAs(file);
      },
      error: () => {
        this.loadingTasksIds$.next(this.loadingTasksIds$.value.filter((uuid) => uuid !== task.uuid));
        const summary = this.translateSrv.instant('messages.downloadTask.error.summary');
        ToastUtils.showError(this.toastService, summary, null);
      },
    });
  }

  public downloadTasks(tasks: Task[]) {
    this.loadingTasksIds$.next([...this.loadingTasksIds$.value, ...tasks.map((task) => task.uuid)]);
    this.taskService.downloadResults(tasks).subscribe({
      next: (value) => {
        this.loadingTasksIds$.next(
          this.loadingTasksIds$.value.filter((uuid) => !tasks.map((task) => task.uuid).includes(uuid)),
        );
        const file = new File([value as BlobPart], `reports.zip`);
        saveAs(file);
      },
      error: () => {
        this.loadingTasksIds$.next(
          this.loadingTasksIds$.value.filter((uuid) => !tasks.map((task) => task.uuid).includes(uuid)),
        );
        const summary = this.translateSrv.instant('messages.downloadTask.error.summary');
        ToastUtils.showError(this.toastService, summary, null);
      },
    });
  }

  public getStatusIndex(task: Task): number {
    switch (task.reportStatus) {
      case TaskStatus.COMPLETE:
        return 1;
      case TaskStatus.RUNNING:
        return 2;
      case TaskStatus.QUEUED:
        return 3;
      default:
        return 0;
    }
  }

  public onHide() {
    this.selectedTasks = [];
    this.firstThreeSelectedTasks.set([]);
    this.finishedTasksGridApi.deselectAll();
  }

  public downloadButtonTaskLength(selectedTasks: Task[]): number {
    return selectedTasks.filter(
      (task) => task.reportStatus === TaskStatus.COMPLETE || task.reportStatus === TaskStatus.DOWNLOADED,
    ).length;
  }

  public downloadButtonDisabled(selectedTasks: Task[]): boolean {
    if (selectedTasks.length === 0) return true;

    return selectedTasks.some(
      (task) => task.reportStatus !== TaskStatus.COMPLETE && task.reportStatus !== TaskStatus.DOWNLOADED,
    );
  }

  public shareButtonDisabled(selectedTasks: Task[]): boolean {
    if (selectedTasks.length === 0) return true;
    return selectedTasks.some((task) => this.isDateOlderThanDateBByX(new Date(task.created), new Date()));
  }

  public isDateOlderThanDateBByX(dateA: Date, dateB: Date): boolean {
    const diffInMs = dateB.getTime() - dateA.getTime();

    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays >= 25;
  }

  public get TaskReportType() {
    return TaskReportType;
  }

  public onSelectDocumentCode(documentCode: DocumentCode | null, select: Select): void {
    if (!documentCode) {
      return;
    }

    const actualText = this.shareEmailFormGroup.controls.description.value || '';
    const documentCodeText = documentCode.inhoud || '';

    this.shareEmailFormGroup.controls.description.setValue(actualText + documentCodeText);
    select.clear();
  }
}
